import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Tooltip, Typography } from '@mui/material';

import styled from 'styled-components';

import { Constants, useStrOfferingData } from '@arrived/common';
import { AssetType, Offering } from '@arrived/models';

import { TextWrapper } from '../../../TextWrapper';
import { OfferingDetailMonetaryValue } from './OfferingDetailValue';

import { getQuarterlyAssetUnderManagementFee } from './utils';

export interface AssetManagementFeeProps {
  offering: Offering;
}

const StyledAssetManagementFee = styled(Box)`
  &.asset-management-fee {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    padding: ${({ theme }) => theme.spacing(2, 3)};
    border: 1px solid ${({ theme }) => theme.palette.neutrals.platinum};

    .asset-management-fee-title {
      text-decoration: underline;
    }
  }

  // for Alexa :)
  &.lil-bit-o-padding {
    margin-top: ${({ theme }) => theme.spacing(3)};
  }

  .asset-management-fees-list-container {
    margin: ${({ theme }) => theme.spacing(1, 0)};
  }
`;

export const LTRAssetManagementFee = ({ offering }: AssetManagementFeeProps) => {
  const assetManagementFee = useMemo(() => getQuarterlyAssetUnderManagementFee(offering), [offering]);

  return (
    <StyledAssetManagementFee className="asset-management-fee" mt={2}>
      <div>
        <Tooltip
          title={
            <FormattedMessage
              id="asset-management-fee-title-tooltip"
              defaultMessage="The Arrived AUM (assets under management) fee is a quarterly fee paid to Arrived out of the rental income, and helps cover the preparation of  tax forms for investors, the distribution of dividends to all investors, procuring insurance policies and filing claims when applicable, ensuring property taxes and loan payments are paid, overseeing financial accounting for properties, and overseeing the third party property manager (competitive analysis of rental rate, review of property improvements, rehab & turn work, and expense management & approval)."
            />
          }
        >
          <TextWrapper
            className="asset-management-fee-title"
            typographyProps={{
              variant: 'body1',
            }}
            formattedMessageProps={{
              id: 'asset-management-fee-title',
              defaultMessage: 'Arrived AUM Fee (Quarterly)',
            }}
          />
        </Tooltip>
      </div>
      <OfferingDetailMonetaryValue value={assetManagementFee} />
    </StyledAssetManagementFee>
  );
};

export const STRAssetManagementFee = ({ offering }: AssetManagementFeeProps) => {
  const { feeData, propertyManagement } = useStrOfferingData(offering.id);

  return (
    <StyledAssetManagementFee className="asset-management-fee lil-bit-o-padding">
      <div>
        <Tooltip
          title={
            <FormattedMessage
              id="asset-management-fee-title-tooltip"
              defaultMessage="The Arrived AUM (assets under management) fee is a quarterly fee paid to Arrived out of the rental income, and helps cover the preparation of  tax forms for investors, the distribution of dividends to all investors, procuring insurance policies and filing claims when applicable, ensuring property taxes and loan payments are paid, overseeing financial accounting for properties, and overseeing the third party property manager (competitive analysis of rental rate, review of property improvements, rehab & turn work, and expense management & approval)."
            />
          }
        >
          <Typography variant="body1" className="asset-management-fee-title">
            <FormattedMessage id="asset-management-fee-title" defaultMessage="Arrived AUM Fee (Quarterly)" />
          </Typography>
        </Tooltip>
        <ul className="asset-management-fees-list-container">
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="asset-management-fee-description"
                defaultMessage="Purchase Price: {aumPropertyPurchasePrice}% of the property purchase price (quarterly)"
                values={{ aumPropertyPurchasePrice: feeData.aumPropertyPurchasePrice }}
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="asset-management-fee-description"
                defaultMessage="Gross Rents Fee: {aumGrossRevenues}% of Gross Revenue"
                values={{ aumGrossRevenues: feeData.aumGrossRevenues }}
              />
            </Typography>
          </li>
        </ul>

        <Tooltip
          title={
            <FormattedMessage
              id="asset-management-fee-title-tooltip"
              defaultMessage="Property Management Fees are paid monthly to the property management partner out of the rental income, and helps cover the operational expenses of the rental unit. Arrived negotiates competitive rates with our property management partners for each offering individually."
            />
          }
        >
          <Typography variant="body1" mt={3} className="asset-management-fee-title">
            <FormattedMessage
              id="asset-management-fee-title"
              defaultMessage="Property Management Fees ({pmName})"
              values={{ pmName: propertyManagement.name }}
            />
          </Typography>
        </Tooltip>
        <ul className="asset-management-fees-list-container">
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="asset-management-fee-description"
                defaultMessage="Gross Rents Fee: {pmGrossRentsText}"
                values={{ pmGrossRentsText: feeData.pmGrossRentsText }}
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="asset-management-fee-description"
                defaultMessage="Furniture, Fixtures and Equipment: Included in the use of proceeds listed above when our property manager partners assist in the furnishing and property update process."
              />
            </Typography>
          </li>
        </ul>
      </div>
    </StyledAssetManagementFee>
  );
};

export const AssetManagementFee = ({ offering }: AssetManagementFeeProps) => {
  if (offering.assetType === AssetType.LTR) {
    return <LTRAssetManagementFee offering={offering} />;
  } else {
    return <STRAssetManagementFee offering={offering} />;
  }
};
